import {forwardRef} from 'react';
import {LoginRequiredAction} from '@liveComponents/Auth';
import ContactOwner from '@liveComponents/LoginRequiredInitialPage/ContactOwner';
import {Button, ButtonProps} from '@components/Button';
import {FormattedMessage} from 'react-intl';
import {AdvertImportType, AdvertMessageType, Currency} from '.cache/__types__';
import {PremiumRequiredAction} from '@liveComponents/Payment/PremiumRequiredAction';
import {getLocalizedRoute, routes} from '@utils/routes';
import Link from 'next/link';
import {useLocale} from '@utils/locale';
import ContactOwnerPremium from "@liveComponents/LoginRequiredInitialPage/ContactOwnerPremium";

export type SendMessageButtonProps = {
    currency: Currency,
    provisionSaved: number,
    messageData: {
        messageType: AdvertMessageType;
        requireLogin: boolean;
        url?: string;
    };
    type: AdvertImportType;
    onContactOwnerModalOpen: () => any;
    size?: ButtonProps['size'];
};

export const SendMessageButton = forwardRef<HTMLButtonElement, SendMessageButtonProps>((
    {
        messageData,
        type,
        onContactOwnerModalOpen,
        size: size = 'lg',
        provisionSaved,
        currency,
    }, ref) => {
    const locale = useLocale();

    if (messageData.messageType === AdvertMessageType.MessageSent) {
        return (
            <Button size={size} className={"w-100"} disabled>
                <FormattedMessage defaultMessage={"Na tento inzerát jste již reagovali"}/>
            </Button>
        )
    }

    if (type === AdvertImportType.Flatio) {
        return (
            <Button ref={ref} size={size} href={messageData.url} className="w-100">
                <FormattedMessage defaultMessage={'Pokračovat na rezervaci'}/>
            </Button>
        )
    }

    if (messageData.messageType === AdvertMessageType.Lead) {
        return (
            <Button ref={ref} size={size} className="w-100" onClick={onContactOwnerModalOpen}>
                <FormattedMessage defaultMessage={'Reagovat na inzerát'}/>
            </Button>
        );
    }

    if (messageData.messageType === AdvertMessageType.Inactive) {
        return (
            <Button ref={ref} href={messageData.url} size={size} className="w-100" disabled>
                <FormattedMessage defaultMessage={'Na inzerát již není možné reagovat'}/>
            </Button>
        );
    }

    if (messageData.messageType === AdvertMessageType.Link) {
        return (
            <Button ref={ref} as={'a'} href={messageData.url} size={size} className="w-100">
                <FormattedMessage defaultMessage={'Více informací'}/>
            </Button>
        );
    }

    if (messageData.messageType === AdvertMessageType.RequirePremium) {
        return (
            <PremiumRequiredAction
                loginRequiredInitialPage={ContactOwnerPremium}
                loginRequiredInitialPageProps={{
                    price: provisionSaved,
                    currency,
                }}
                lead={<FormattedMessage defaultMessage={'Buďte první'}/>}
                text={
                    <FormattedMessage
                        defaultMessage={
                            'Odepisovat na právě přidané inzeráty můžete po dobu 4\u00a0měsíců s <link>Premium profilem</link> za <strong>jednorázový poplatek 299\u00a0Kč</strong>. Díky tomu získáte šanci být první zájemce!'
                        }
                        values={{
                            link: (chunks: any) => (
                                <Link href={getLocalizedRoute(routes.serviceCentrePremium, locale)} passHref>
                                    <a target="_blank">{chunks}</a>
                                </Link>
                            ),
                            strong: (chunks: any) => <strong>{chunks}</strong>,
                        }}
                    />
                }
            >
                <Button ref={ref} size={size} className="w-100" onClick={onContactOwnerModalOpen}>
                    <FormattedMessage defaultMessage={'Kontaktovat majitele'}/>
                </Button>
            </PremiumRequiredAction>
        );
    }

    if (messageData.requireLogin) {
        return (
            <LoginRequiredAction initialPage={ContactOwner}>
                <Button ref={ref} size={size} className="w-100" onClick={onContactOwnerModalOpen}>
                    <FormattedMessage defaultMessage={'Kontaktovat majitele'}/>
                </Button>
            </LoginRequiredAction>
        );
    }

    return (
        <Button ref={ref} size={size} className="w-100" onClick={onContactOwnerModalOpen}>
            <FormattedMessage defaultMessage={'Kontaktovat majitele'}/>
        </Button>
    );
});
